import { DropdownMenuTriggerProps } from '@radix-ui/react-dropdown-menu';
import { format } from 'date-fns';
import { de } from 'date-fns/locale/de';
import { XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CalendarIcon from '@/shared/assets/calendar.svg?react';
import { FilterButton } from '@/shared/components/filter/filter-button';
import { FilterChip } from '@/shared/components/filter/filter-chip';
import { FilterProps } from '@/shared/components/filter/filter-types';
import { Button } from '@/shared/components/ui/button';
import { CustomDatePickerDialog } from '@/shared/components/ui/custom-date-picker-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';

export const Filter = ({
  filters,
  trigger,
  onReset,
  onApply,
  onOpenChange,
  activeCount,
  showPredefinedPeriods = false,
}: FilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [datePickerOpenIndex, setDatePickerOpenIndex] = useState<number | null>(
    null,
  );
  const { t } = useTranslation('shared', { keyPrefix: 'Filter' });
  const chips: { key: string; label: string; onClear: () => void }[] = [];

  filters.forEach((f, idx) => {
    if (f.type === 'dateRange') {
      if (f.from && f.to) {
        chips.push({
          key: `${idx}-from-${f.from}-to-${f.to}`,
          label: `${formatDate({ date: f.from, formatType: 'digits' })} - ${formatDate({ date: f.to, formatType: 'digits' })}`,
          onClear: () => {
            f.onRemoveFrom();
            f.onRemoveTo();
          },
        });
        return;
      } else if (f.from) {
        chips.push({
          key: `${idx}-from-${f.from}`,
          label: formatDate({ date: f.from, formatType: 'digits' }),
          onClear: () => f.onRemoveFrom(),
        });
      } else if (f.to) {
        chips.push({
          key: `${idx}-to-${f.to}`,
          label: formatDate({ date: f.to, formatType: 'digits' }),
          onClear: () => f.onRemoveTo(),
        });
      }
      return;
    }
    if (!f.options || f.options.length === 0) return;
    if (f.type === 'multiple') {
      (f.values ?? []).forEach((val, i) => {
        const opt = f.options?.find((o) => o.value === val);
        if (!opt) return;
        chips.push({
          key: `${idx}-${i}-${val}`,
          label: opt.label,
          onClear: () => {
            f.onRemove(val);
          },
        });
      });
    } else if (f.value) {
      const opt = f.options?.find((o) => o.value === f.value);
      if (opt) {
        chips.push({
          key: `${idx}-single-${f.value}`,
          label: opt.label,
          onClear: () => f.onSelect?.(undefined),
        });
      }
    }
  });

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        onOpenChange?.(open);
      }}
    >
      {trigger ?? (
        <FilterTrigger>
          <FilterButton activeCount={activeCount} />
        </FilterTrigger>
      )}
      <FilterContent
        onClose={() => setIsOpen(false)}
        className="w-[267px] max-w-80"
      >
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {chips.map((chip) => (
              <FilterChip
                key={chip.key}
                label={chip.label}
                onClear={chip.onClear}
              />
            ))}
          </div>
        )}
        {filters.map((filter, idx) => {
          const selectedCount =
            filter.type === 'multiple'
              ? filter.values?.filter((v) =>
                  filter.options?.some((o) => o.value === v),
                ).length || undefined
              : undefined;

          return filter.type === 'dateRange' ? (
            <div
              key={`dateRange-${idx}`}
              className={cn(
                'flex flex-col gap-2 rounded-[6px] border border-dashed border-white/15 p-2',
              )}
            >
              <span className="text-2xs">{t('filter_range')}</span>
              <CustomDatePickerDialog
                isOpen={datePickerOpenIndex === idx}
                onOpenChange={(open) =>
                  setDatePickerOpenIndex(open ? idx : null)
                }
                startDate={filter.from}
                endDate={filter.to}
                onDateRangeSelect={(start, end) => {
                  filter.onSelectFrom(start);
                  filter.onSelectTo(end);
                }}
                title={t('select_time_period')}
                showPredefinedPeriods={showPredefinedPeriods}
              >
                <div className="flex cursor-pointer flex-row items-center gap-2">
                  <div className="flex min-w-[100px] items-center justify-between gap-1 rounded-2xs border border-white/20 bg-transparent px-3 py-2 text-2xs">
                    <span
                      className={filter.from ? 'text-white' : 'text-white/60'}
                    >
                      {filter.from
                        ? format(filter.from, 'dd.MM.yyyy', { locale: de })
                        : filter.fromPlaceholder}
                    </span>
                    <CalendarIcon className="size-4 flex-shrink-0 text-white/60" />
                  </div>
                  <span className="text-white/60">-</span>
                  <div className="flex min-w-[100px] items-center justify-between gap-1 rounded-2xs border border-white/20 bg-transparent px-3 py-2 text-2xs">
                    <span
                      className={filter.to ? 'text-white' : 'text-white/60'}
                    >
                      {filter.to
                        ? format(filter.to, 'dd.MM.yyyy', { locale: de })
                        : filter.toPlaceholder}
                    </span>
                    <CalendarIcon className="size-4 flex-shrink-0 text-white/60" />
                  </div>
                </div>
              </CustomDatePickerDialog>
            </div>
          ) : (
            filter.options && filter.options.length > 0 && (
              <div
                className="flex items-center gap-1"
                key={`${filter.label}-${idx}`}
              >
                <span className="min-w-0 flex-1">
                  <Select
                    value={
                      filter.type === 'multiple' ? '' : (filter.value ?? '')
                    }
                    onValueChange={(val) => filter.onSelect?.(val)}
                  >
                    <SelectTrigger
                      className="overflow-x-clip"
                      variant="transparent"
                      size="sm"
                    >
                      <SelectValue
                        asLabelInput={false}
                        placeholder={
                          filter.placeholder +
                          (selectedCount ? ` (${selectedCount})` : '')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {filter.options.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          subLabel={option.subLabel}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </span>
              </div>
            )
          );
        })}
        <div className="flex flex-row gap-2">
          <Button
            variant="outline"
            btnType="secondary"
            className="w-full"
            onClick={() => {
              onReset?.();
            }}
          >
            {t('reset')}
          </Button>
          <Button
            variant="greenGradient"
            btnType="secondary"
            className="w-full"
            onClick={() => {
              onApply?.();
              setIsOpen(false);
            }}
          >
            {t('apply')}
          </Button>
        </div>
      </FilterContent>
    </DropdownMenu>
  );
};

export const FilterTrigger = ({
  className,
  children,
  ...props
}: DropdownMenuTriggerProps) => {
  return (
    <DropdownMenuTrigger className={className} {...props}>
      {children}
    </DropdownMenuTrigger>
  );
};

export const FilterContent = ({
  children,
  onClose,
  className,
}: {
  children: React.ReactNode;
  onClose: () => void;
  className?: string;
}) => {
  const { t } = useTranslation('shared', { keyPrefix: 'Filter' });

  return (
    <DropdownMenuContent
      className={cn('w-64 min-w-64', className)}
      side={'top'}
      align={'end'}
      sideOffset={20}
    >
      <div className="mx-4 my-2 flex flex-row items-center">
        <DropdownMenuLabel className="flex-1 p-0 text-xs font-normal">
          {t('filter')}
        </DropdownMenuLabel>
        <XIcon
          className="cursor-pointer text-white"
          size={'15px'}
          onClick={() => onClose()}
        />
      </div>
      <DropdownMenuSeparator className="my-2 h-[0.75px]" />
      <div className="m-2 mt-4 flex flex-col gap-4">{children}</div>
    </DropdownMenuContent>
  );
};
